import React, { useEffect } from 'react';
import { useMonitoringUserActivity } from '../../hooks/queries';
import type { RequestTimelineEntry } from '../../types/monitoring';

function fmtAge(mins: number | null): string {
  if (mins === null) {
    return '—';
  }
  if (mins < 0) {
    return 'just now';
  }
  if (mins < 60) {
    return `${mins}m ago`;
  }
  if (mins < 1440) {
    return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
  }
  return `${Math.floor(mins / 1440)}d ago`;
}

function fmtTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

interface UserActivityDetailPanelProps {
  email: string;
  hours: number;
  onClose: () => void;
}

function ContextChips({ entry }: { entry: RequestTimelineEntry }) {
  const ctx = entry.context;
  if (!ctx?.resourceId && !ctx?.productSku && !ctx?.filters) {
    return null;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.25rem' }}>
      {ctx.productSku && (
        <span style={{
          fontSize: '0.65rem', background: '#f5f3ff', color: '#6d28d9',
          padding: '0.1rem 0.35rem', borderRadius: 4, fontWeight: 600,
        }}
        >
          Product: {ctx.productSku}
        </span>
      )}
      {ctx.resourceId && (
        <span style={{
          fontSize: '0.65rem', background: '#eff6ff', color: '#1d4ed8',
          padding: '0.1rem 0.35rem', borderRadius: 4,
        }}
        >
          ID: {ctx.resourceId}
        </span>
      )}
      {ctx.filters && Object.entries(ctx.filters).map(([key, value]) => (
        <span
          key={key}
          style={{
            fontSize: '0.65rem', background: '#f1f5f9', color: '#475569',
            padding: '0.1rem 0.35rem', borderRadius: 4,
          }}
        >
          {key}: {value}
        </span>
      ))}
    </div>
  );
}

export function UserActivityDetailPanel({ email, hours, onClose }: UserActivityDetailPanelProps) {
  const { data, isLoading, error, refetch } = useMonitoringUserActivity(email, hours, true);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const panelWidth = window.innerWidth < 768 ? '100%' : 400;

  return (
    <>
      <div
        role="presentation"
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.25)', zIndex: 40,
        }}
      />
      <div
        role="dialog"
        aria-labelledby="user-activity-panel-title"
        data-testid="monitoring-user-detail-panel"
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: panelWidth,
          background: '#fff', borderLeft: '1px solid #e2e8f0', zIndex: 50,
          display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
          fontFamily: 'system-ui, sans-serif', color: '#0f172a',
        }}
      >
        <div style={{
          padding: '1rem 1.25rem', borderBottom: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        }}
        >
          <div>
            <div
              id="user-activity-panel-title"
              style={{ fontWeight: 800, fontSize: '1rem' }}
            >
              {data?.name || email}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>{email}</div>
            {data && (
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 4 }}>
                <strong style={{ color: '#2563eb' }}>{data.totalRequests}</strong> requests · last {fmtAge(data.minsAgo)} · {hours}h window
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close user activity detail"
            style={{
              border: 'none', background: '#f1f5f9', borderRadius: 6,
              width: 32, height: 32, cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem' }}>
          {isLoading && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
              Loading user activity…
            </div>
          )}

          {error && !isLoading && (
            <div style={{ textAlign: 'center', padding: '1.5rem' }}>
              <div style={{ color: '#dc2626', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
                {(error as { message?: string }).message ?? 'Failed to load user activity.'}
              </div>
              <button
                type="button"
                onClick={() => refetch()}
                style={{
                  padding: '0.4rem 0.75rem', borderRadius: 6, border: '1px solid #e2e8f0',
                  background: '#fff', cursor: 'pointer', fontSize: '0.8rem',
                }}
              >
                Retry
              </button>
            </div>
          )}

          {data && !isLoading && data.totalRequests === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8', fontSize: '0.85rem' }}>
              No activity recorded for this user in the selected time window.
            </div>
          )}

          {data && data.totalRequests > 0 && (
            <>
              <div style={{
                fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.08em', color: '#64748b', marginBottom: '0.5rem',
              }}
              >
                By Application Area
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                {data.areas.map((area) => (
                  <div key={area.area} style={{ marginBottom: '0.5rem' }}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: 2,
                    }}
                    >
                      <span style={{ fontWeight: 600 }}>{area.area}</span>
                      <span style={{ color: '#64748b' }}>{area.count} ({area.percentage}%)</span>
                    </div>
                    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', width: `${area.percentage ?? 0}%`,
                        background: '#2563eb', borderRadius: 3,
                      }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div style={{
                fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.08em', color: '#64748b', marginBottom: '0.5rem',
              }}
              >
                Recent Requests
              </div>
              {data.timelineTruncated && (
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                  Showing {data.timeline.length} of {data.timelineTotal} requests
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {data.timeline.map((entry, idx) => (
                  <div
                    key={`${entry.timestamp}-${entry.path}-${idx}`}
                    style={{
                      padding: '0.5rem 0.6rem', background: '#f8fafc',
                      borderRadius: 6, border: '1px solid #f1f5f9',
                    }}
                  >
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{fmtTimestamp(entry.timestamp)}</div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, marginTop: 2 }}>
                      {entry.area} — {entry.action}
                    </div>
                    <div style={{ fontSize: '0.68rem', color: '#cbd5e1', marginTop: 2 }}>{entry.path}</div>
                    <ContextChips entry={entry} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
